import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';

import { undoAction } from '../../../../ucc3/ucc3.services';
import { useSnackbar } from '../../../../../common/notification';
import { useModal } from '../../../../../common/context';
import { StyledModalSubmitButton } from '../../../../../common/global-styles';

import { IOpenModalArgs, UndoActionType, IUseUndoRemoveModalProps } from '../../../../ucc3/models';
import { CloseIconButton } from '../../../../../common/global-styles/CloseIconButton';
import Loader from '../../../../../common/components/loader';

const undoActionContent: Record<UndoActionType, string> = {
  Remove: 'Are you sure you want to undo the remove?',
  Edit: 'Are you sure you want to undo the edits?',
};

const useUndoActionModal = ({ getData }: IUseUndoRemoveModalProps) => {
  const { handleModalOpen, handleModalClose, handleSetModalActions } = useModal();
  const theme = useTheme();
  const { Snack } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenModal = ({ debsecId, filingId, action }: IOpenModalArgs) => {
    handleModalOpen({
      title: `Undo ${action}`,
      modalContent: <Typography variant="h4">{undoActionContent[action]}</Typography>,
      xButtonAction: <CloseIconButton onClick={handleModalClose} />,
      modalActions: (
        <Box
          sx={{
            display: 'flex',
            gap: { xs: theme.convert.pxToRem(20), sm: theme.convert.pxToRem(34) },
            flexDirection: 'row-reverse',
          }}
        >
          <StyledModalSubmitButton
            autoFocus
            onClick={async (e) => {
              setIsLoading(true);
              if (filingId) {
                const { notOk, messages } = await undoAction({ filingId, debsecId });
                if (!notOk) {
                  Snack.success(` ${action} was successfully undone.`);
                  getData(filingId);
                  setIsLoading(false);
                  handleModalClose(e);
                } else {
                  const message = messages
                    ?.map(({ message }: { message: string }) => message)
                    .join(' ');
                  Snack.error(
                    typeof message === 'string' ? message : 'Your request failed to delete.'
                  );
                  setIsLoading(false);
                  handleModalClose(e);
                }
              } else {
                Snack.error('Missing filing ID number.');
                setIsLoading(false);
                handleModalClose(e);
              }
            }}
            variant="contained"
          >
            Proceed
          </StyledModalSubmitButton>
          <StyledModalSubmitButton
            onClick={(e) => {
              handleModalClose(e);
              Snack.info('Action canceled.');
            }}
            variant="outlined"
          >
            Cancel
          </StyledModalSubmitButton>
        </Box>
      ),
    });
  };

  useEffect(() => {
    handleSetModalActions(
      <Box
        sx={{
          display: 'flex',
          gap: { xs: theme.convert.pxToRem(20), sm: theme.convert.pxToRem(34) },
          flexDirection: 'row-reverse',
        }}
      >
        <StyledModalSubmitButton
          autoFocus
          startIcon={isLoading ? <Loader /> : null}
          disabled={isLoading}
          variant="contained"
        >
          {isLoading ? 'Proceeding' : 'Proceed'}
        </StyledModalSubmitButton>
        <StyledModalSubmitButton
          onClick={(e) => {
            handleModalClose(e);
            Snack.info('Action canceled.');
          }}
          variant="outlined"
        >
          Cancel
        </StyledModalSubmitButton>
      </Box>
    );
  }, [isLoading]);

  return { handleOpenModal };
};

export default useUndoActionModal;
